import { redirect } from '@tanstack/react-router'
import { buildSafeRedirect, resolvePostLoginTarget } from '@/lib/auth-redirect'
import { ensureAuthUser } from '@/lib/ensure-auth-user'
import { useAuthStore } from '@/stores/auth-store'

function throwSignInRedirect(locationHref: string): never {
  const auth = useAuthStore.getState().auth
  auth.reset()
  const redirectPath = buildSafeRedirect(locationHref)
  throw redirect({
    to: '/sign-in',
    search: redirectPath ? { redirect: redirectPath } : {},
  })
}

/** Ensure a persisted session is loaded before authenticated routes render. */
export async function requireAuthenticatedSession(locationHref: string): Promise<void> {
  const auth = useAuthStore.getState().auth

  if (!auth.getEffectiveAccessToken()) {
    throwSignInRedirect(locationHref)
  }

  await ensureAuthUser()

  if (!auth.getEffectiveAccessToken()) {
    throwSignInRedirect(locationHref)
  }
}

/** Send already-authenticated users away from the sign-in page. */
export async function redirectIfAuthenticated(options: {
  redirect?: string
  token?: string
}): Promise<void> {
  // Impersonation / magic-link flows must consume ?token= on the sign-in page.
  if (options.token) return

  const auth = useAuthStore.getState().auth
  if (!auth.getEffectiveAccessToken()) return

  await ensureAuthUser()

  const user = useAuthStore.getState().auth.user
  if (!user) return

  throw redirect({
    to: resolvePostLoginTarget(options.redirect, user.role || user.type),
    replace: true,
  })
}
